/**
 * Resource history drawer backed by `/api/admin/audit?resource_type=&resource_id=`.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v1
 */
import { Copy, GitCompare, X } from 'lucide-react';
import { Chip } from '../components/chip';
import { useResourceAuditHistory } from '../../lib/api';
import { getAuditDiffRows, groupAuditEntriesByDay } from '../../lib/audit';

export function ResourceHistoryPanel({
    resourceType,
    resourceId,
    onClose
}: {
    resourceType: string;
    resourceId: string;
    onClose: () => void;
}) {
    const history = useResourceAuditHistory(resourceType, resourceId);
    const groups = groupAuditEntriesByDay(history.data ?? []);

    return (
        <>
            <header className="detail-panel-header">
                <div className="audit-drawer-icon" data-source="action">
                    <GitCompare size={18} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 className="name">Resource history</h2>
                    <p className="email">{resourceType}</p>
                    <button
                        className="copyable-id"
                        type="button"
                        onClick={() => void navigator.clipboard?.writeText(resourceId)}
                    >
                        <span>{resourceId}</span>
                        <Copy size={11} />
                    </button>
                </div>
                <button className="close" onClick={onClose} aria-label="Close resource history">
                    <X size={16} />
                </button>
            </header>

            <section className="detail-panel-section">
                <h3>Timeline</h3>
                {history.isLoading && <div className="empty-state">Loading history…</div>}
                {history.error && (
                    <div className="empty-state">
                        <div className="title">History unavailable</div>
                        <div>
                            {history.error instanceof Error
                                ? history.error.message
                                : 'The audit endpoint did not return resource history.'}
                        </div>
                    </div>
                )}
                {!history.isLoading && !history.error && groups.length === 0 && (
                    <div className="empty-state">
                        <div className="title">No history for this resource</div>
                        <div>Try copying the immutable ID for backend lookup.</div>
                    </div>
                )}
                <div className="resource-history">
                    {groups.map((group) => (
                        <section key={group.day}>
                            <h4>{group.day}</h4>
                            {group.rows.map((entry) => {
                                const diffs = getAuditDiffRows(entry).slice(0, 3);
                                return (
                                    <article className="resource-history-row" key={entry.id}>
                                        <header>
                                            <span className="mono">{entry.ts}</span>
                                            <Chip
                                                tone={entry.source === 'action' ? 'purple' : 'info'}
                                            >
                                                {entry.source}
                                            </Chip>
                                            <Chip
                                                tone={
                                                    entry.severity === 'destructive'
                                                        ? 'danger'
                                                        : entry.severity === 'sensitive'
                                                          ? 'warning'
                                                          : 'muted'
                                                }
                                            >
                                                {entry.severity}
                                            </Chip>
                                        </header>
                                        <div className="resource-history-title">{entry.action}</div>
                                        <div className="text-muted">
                                            {entry.actor || 'system'}
                                            {entry.reason ? ` · ${entry.reason}` : ''}
                                        </div>
                                        {diffs.length > 0 && (
                                            <ul>
                                                {diffs.map((diff) => (
                                                    <li key={diff.field}>
                                                        <span>{diff.field}</span>
                                                        <code>{diff.before}</code>
                                                        <code>{diff.after}</code>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </article>
                                );
                            })}
                        </section>
                    ))}
                </div>
            </section>
        </>
    );
}
